import { ref } from 'vue'

export interface FormValidation {
  validate: (value: any) => boolean | string
  validateOnBlur: boolean
  validationState: Ref<boolean | string>
  runValidation: (value?: any, isRequired?: boolean) => boolean | string
  resetValidation: () => void
  handleBlur: (value?: any, isRequired?: boolean) => void
  isRequired: boolean
}

export function useFormValidation(
  validate?: (value: any) => boolean | string,
  initialValue?: any,
  isRequired = false,
): FormValidation {
  const validationState = ref<boolean | string>(true)
  const isRequiredRef = ref(isRequired)

  const validateFn = (value: any, checkRequired = isRequiredRef.value): boolean | string => {
    // First handle required validation if needed
    if (checkRequired) {
      // Check if value is empty (handling different value types)
      const isEmpty
        = value === undefined
          || value === null
          || value === ''
          || (Array.isArray(value) && value.length === 0)

      if (isEmpty) {
        return 'This field is required'
      }
    }

    // Then run custom validation if provided
    if (!validate) return true
    return validate(value)
  }

  const runValidation = (value: any = initialValue, checkRequired = isRequiredRef.value): boolean | string => {
    const result = validateFn(value, checkRequired)
    validationState.value = result
    return result
  }

  const resetValidation = (): void => {
    validationState.value = true
  }

  const handleBlur = (value: any = initialValue, checkRequired = isRequiredRef.value): void => {
    if (validateOnBlur.value) {
      runValidation(value, checkRequired)
    }
  }

  // Default behavior is to validate on blur
  const validateOnBlur = ref(true)

  return {
    validate: validateFn,
    validateOnBlur,
    validationState,
    runValidation,
    resetValidation,
    handleBlur,
    isRequired: isRequiredRef.value,
  }
}
